// Message box for the workspace chat, with the video's message allowance underneath.
import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FREE_MESSAGE_LIMIT, MAX_MESSAGE_CHARS } from './chatModel';

// Up to this many messages the allowance is drawn as one pip each; above it, as a bar
const MAX_PIPS = 12;

const isTouch = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

export function Allowance({ remaining, limit = FREE_MESSAGE_LIMIT }) {
  const tone = remaining === 0 ? 'is-empty' : remaining <= Math.min(3, limit / 2) ? 'is-low' : '';
  const label = remaining === 0 ? 'No messages left' : `${remaining} of ${limit} messages left`;
  return (
    <div
      className={`wsc-allowance ${tone}`}
      title={`Each video includes ${limit} messages, shared by all of its chats. Messages that fail to get an answer aren't counted.`}
    >
      {limit <= MAX_PIPS ? (
        <span className="wsc-pips" aria-hidden="true">
          {Array.from({ length: limit }, (_, i) => <i key={i} className={i < remaining ? 'is-left' : ''} />)}
        </span>
      ) : (
        <span className="wsc-meter" aria-hidden="true">
          <i style={{ width: `${(100 * remaining) / limit}%` }} />
        </span>
      )}
      <span className="wsc-allowance-text">{label}<span className="wsc-allowance-scope"> for this video</span></span>
    </div>
  );
}

export default function ChatComposer({
  value, onChange, onSend, textareaRef,
  placeholder, disabled, sendBlocked, remaining, limit,
}) {
  const localRef = useRef(null);
  const ref = textareaRef || localRef;

  // grow with the text, up to the CSS max-height
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value, ref]);

  const canSend = !disabled && !sendBlocked && value.trim().length > 0;
  const submit = e => {
    e.preventDefault();
    if (canSend) onSend();
  };
  const onKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !isTouch()) {
      e.preventDefault();
      if (canSend) onSend();
    }
  };
  const nearLimit = value.length > MAX_MESSAGE_CHARS * 0.8;

  return (
    <form className="wsc-composer" onSubmit={submit}>
      <div className={`wsc-input ${disabled ? 'is-disabled' : ''}`}>
        <textarea
          ref={ref}
          rows={1}
          value={value}
          onChange={e => onChange(e.target.value.slice(0, MAX_MESSAGE_CHARS))}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={MAX_MESSAGE_CHARS}
          aria-label="Message Bump AI"
        />
        <button type="submit" className="wsc-send" disabled={!canSend} aria-label="Send message" title="Send">
          <FontAwesomeIcon icon={faArrowUp} />
        </button>
      </div>
      <div className="wsc-composer-foot">
        <Allowance remaining={remaining} limit={limit} />
        {nearLimit
          ? <span className="wsc-charcount">{value.length.toLocaleString()} / {MAX_MESSAGE_CHARS.toLocaleString()}</span>
          : <span className="wsc-disclaimer">Bump AI can make mistakes. Check important info.</span>}
      </div>
    </form>
  );
}
